/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from "react";
import MediaPrepHeaderSection from "../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../common/commonComponents/mediaPrepPageHeader";
import { useTranslation } from 'react-i18next';
import MediaPreparationTable from "../../../common/commonComponents/ProductMasterTable";
import { getProductMasterColumns } from "./partials/column";
import { MediaPowderProductMasterService } from "../../../../service/mediaPowderProductMaster";
import { useEffect, useState } from "react";
import ProductMasterFilterBar from "./partials/filterBar";
import Pagination from "../../../../widgets/pagination";
import { DEFAULT_PAGE_SIZE } from "../../../../constants/constants";
import AddPowderProduct from "./views/AddPowderProduct";
import ProductMasterDetails from "./views/PowderProductDetails";
import CustomToast from "../../../../widgets/toaster";
import { userService } from "../../../../service/userService";
import AlertModal from "../../../../widgets/alertModal";


interface ProductMasterProps {
  onAddProduct?: () => void;
  onViewProduct?: (id: string) => void;
}
const MediaPowderProductMaster: React.FC<ProductMasterProps> = ({
    onAddProduct,
    onViewProduct,
  }) => {
  const { t } = useTranslation();
  const [mediaTypes, setMediaTypes] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [mediaTypeFilter, setMediaTypeFilter] = useState("");

  const [manufacturerFilter, setManufacturerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [products, setProducts] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [view, setView] = useState<"list" | "create" | "details">("list");
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("");
  const [loading, setLoading] = useState(false);

  const [showStatusAlert, setShowStatusAlert] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState({
    id: "",
    status: "",
  });

  const handleStatusAlert = (id: string, status: string) => {
    setSelectedStatus({
      id,
      status,
    });
    setShowStatusAlert(true);
  };

  const handleStatusChange = async () => {
    const updatedStatus =
      selectedStatus.status === "active" ? "inactive" : "active";

    const payload = {
      id: selectedStatus.id,
      status: updatedStatus,
      interface_name: "Update Powder Product Master",
    };

    try {
      const response =
        await MediaPowderProductMasterService.changeMediaPowderStatus(payload);

      if (response.status && response.statusCode === 200) {
        getProductMasterList();
        setToastMessage(response.message);
        setToastType("success");
      } else {
        setToastMessage(response.message);
        setToastType("error");
      }

      setShowToast(true);
      setShowStatusAlert(false);
    } catch (error) {
      console.error(error);
      setShowStatusAlert(false);
    }
  };

  const [sort, setSort] = useState("id");
        const [order, setOrder] = useState("desc");
        const handleSort = (key: string, value: boolean) => {
          setSort(key);
          setOrder(value ? "asc" : "desc");
          setCurrentPage(1);
        };

  const [permission, setPermission] = useState<any>([]);
	const getPermissionByRole = async (role: number) => {
		try {
		const response = await userService.getPermissionByRole(role);

		if (response.status && response.statusCode === 200) {
			setPermission(response.data.permissions);
		}
		} catch (error) {
		console.error(error);
		}
	};

	useEffect(() => {
		const storedData = localStorage.getItem("userDetails");

		if (storedData) {
		const storedObject = JSON.parse(storedData);
		getPermissionByRole(storedObject[0].role_master_id);
		}
	}, []);

  // Fetch media types list for the media type filter
  const getMediaTypes = async () => {
    try {
      const response = await MediaPowderProductMasterService.getMediaMasterList();

      if (response?.status && response?.statusCode === 200) {
        const mappedMediaTypes = response.data.map((item: any) => ({
          id: item.id,
          name: item.plate_type,
        }));

        setMediaTypes(mappedMediaTypes);
      }
    } catch (error) {
      console.error("Media Master API error:", error);
    }
  };

  // Fetch product master list with optional filters
  const getProductMasterList = async () => {
    setLoading(true);
    try {
      const response =
        await MediaPowderProductMasterService.getMediaProductDefinitions(currentPage, pageSize, sort, order, searchTerm, mediaTypeFilter, statusFilter);

      if (response.status && response.statusCode === 200) {
        const mappedData = response.data.map((item: any) => {
          const finalPh = item.final_ph;
          const tolerance = item.final_ph_tolerance;

          let finalPhValue = "-";
          if (finalPh === null && tolerance === null) {
            finalPhValue = "-";
          } else {
            finalPhValue = `${finalPh ?? "-"} ± ${tolerance ?? "-"}`;
          }

          let initialPhValue = "-";
          if (item.initial_ph === null && item.initial_ph_tolerance === null) {
            initialPhValue = "-";
          } else {
            initialPhValue = `${item.initial_ph ?? "-"} ± ${item.initial_ph_tolerance ?? "-"}`;
          }

          return {
            id: item.id,
            name: item.media_name,
            type: item.media_type,
            mf: item.manufacturer_name,
            cat: item.catalog_number,
            initialPh: initialPhValue,
            finalPh: finalPhValue,
            shelfLife: item.shelf_life,
            status: item.status,
          };
        });

        setProducts(mappedData);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
      } else {
        setProducts([]);
      }
    } catch (error) {
      console.error("Product Master API error:", error);
    }
    finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);

    if (size) {
      setPageSize(size);
    }
  };

  const showToastMessage = (message: string, type: string) => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };


  useEffect(() => {
    getMediaTypes();
  }, []);

  useEffect(() => {
    getProductMasterList();
  }, [currentPage, pageSize, sort, order, searchTerm, mediaTypeFilter, statusFilter]);


  if (view === "create") {
    return (
      <>
      <CustomToast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={2500}
        message={toastMessage}
        toastType={toastType}
      />

      <AddPowderProduct
        showToastMessage={showToastMessage}
        onCancel={() => {
          setView("list");
          getProductMasterList();
        }}
        onSave={() => {
          setView("list");
          getProductMasterList();
        }}
      />
    </>  
    );
  }

  if (view === "details" && selectedProductId) {
    return (
      <ProductMasterDetails
        productId={selectedProductId}
        onBack={() => {
          setView("list");
          getProductMasterList();
        }}
      />
    );
  }

  return (
  <div>
  <CustomToast
    show={showToast}
    onClose={() => setShowToast(false)}
    delay={2500}
    message={toastMessage}
    toastType={toastType}
  />
  <MediaPrepHeaderSection />
  <MediaPrepPageHeader pageHeader={t('mediaPowderProductMaster.pageTitle')} pageDescription={t('pageDescription.mediaPowderProductMaster.description')} />
    <div className="media-prep-scope px-8 pb-13 pt-3 max-w-full animate-in fade-in duration-500 min-h-full">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-2">
        <div className="space-y-1">
            {/* <h2 className='headText1 m-0 d-flex align-items-center' style={{ fontSize: '18px', height: '100%' }}>
            {t('mediaPowderProductMaster.pageTitle')}
            </h2>
            <p className="text-slate-400 text-[12.5px] font-medium">
            {t('mediaPowderProductMaster.description')}
            </p> */}
        </div>

        <div className="ml-[520px] mt-1">
          {permission?.includes("add_media_powder_product") && (
            <button
            onClick={() => setView("create")}
            className="bg-[#FF7043] text-white flex items-center gap-1 px-4 py-2 rounded-lg shadow-[0_8px_20px_rgba(255,112,67,0.2)] font-semibold text-xs uppercase tracking-wide hover:brightness-110 active:scale-95 transition-all border-none"
            >
            <span className="material-symbols-outlined">{t('mediaPowder.icons.add_box')}</span> {t('mediaPowderProductMaster.actions.AddMediaProduct')}
            </button>
          )}
        </div>
    </div>

      {/* Search */}
      <ProductMasterFilterBar
        mediaTypes={mediaTypes}
        selectedMediaType={mediaTypeFilter}
        manufacturerFilter={manufacturerFilter}
        statusFilter={statusFilter}
        searchTerm={searchInput}
        totalElements={totalElements}
        onManufacturerChange={setManufacturerFilter}
        onStatusChange={setStatusFilter}

        onSearchChange={(val) => {
          setSearchInput(val);

          if (!val) {
            setSearchTerm("");
            setCurrentPage(1);
          }
        }}

        onMediaTypeChange={(val) => {
          setMediaTypeFilter(val);
          setCurrentPage(1);
        }}
        onClearFilters={() => {
          setMediaTypeFilter("");
          setSearchTerm("");
          setCurrentPage(1);
          setSearchInput("");
          setManufacturerFilter("");
          setStatusFilter("");
        }}

        onSearchClick={() => {
          setSearchTerm(searchInput);
          setCurrentPage(1);
        }}
      />

      {/* Table */}
      <MediaPreparationTable
        columns={getProductMasterColumns(
          t,
          handleStatusAlert,
          permission?.includes("edit_powder_product")
        )}
        data={products}
        loading={loading}
        currentPage={currentPage}
        pageSize={pageSize}
        onSortClick={handleSort}
        // onStatusChange={handleStatusAlert}
        renderAction={(row) => (
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setSelectedProductId(row.id);
                setView("details");
              }}
              className="flex items-center justify-center gap-1 w-[90px] h-[26px] bg-[rgb(54,64,93)] text-white rounded-lg font-black text-[8px] uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all shadow-sm border-none outline-none focus:outline-none"
            >
              <span className="material-symbols-outlined text-base">{t('mediaPowder.icons.visibility')}</span>{t('mediaPowderProductMaster.actions.view')}
            </button>
          </div>
        )}
      />
    </div>
    <Pagination
      page={currentPage}
      totalPages={totalPages}
      handlePageChange={handlePageChange}
    />
    <AlertModal
      show={showStatusAlert}
      onHide={() => setShowStatusAlert(false)}
      title={t("common.alert")}
      message="Do you want to change the status?"
      onConfirm={handleStatusChange}
    />
   </div>
  );
};

export default MediaPowderProductMaster;